import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  HStack,
  Spacer,
  Text,
  useDisclosure,
  useMediaQuery,
  VStack
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSettingsContext } from "../../contexts/SettingsContext";
import { FiEdit } from "react-icons/fi";
import ScenarioCard from "../../components/ScenarioCard";
import { useNavigate } from "react-router-dom";
import { useQuery } from "../../custom/useQuery";
import ScenarioAddModal from "./components/ScenarioCreateModal";
import APButton from "../../components/APButton";
import WordsSelection from "./components/WordsSelection";
import ScenarioPreviewModal from "../MainScreen/components/ScenarioPreviewModal";
import CancelConfirmModal from "../../components/modals/CancelConfirmModal";
import { chunkArray, getTagLabels, resolveWordList, saveToMyList } from "../../utils";
import ShortUniqueId from 'short-unique-id';
import Profile from "../Profile";
import NavTopBar from "../../components/NavTopBar";
import CallToLoginView from "../Profile/components/CallToLoginView";
import APCommonDialog from "../../components/modals/APCommonDialog";
import APIconButton from "../../components/APIconButton";
import { Back, Close, Plus } from "../../components/APAssetAtlas";
import { IExerciserSDK } from "ap-exerciser-sdk";
import { VocabScenario, VocabWord } from "../../VocabModel";
import { SquidexScenarioData } from "ap-exerciser-sdk/models";
import { useVocabIntl } from "../../hooks/useVocabTranslation";
import { guestScenariosStorageKey } from "../../constants/vocabulaire";
import APFooter from "../../components/APFooter";

interface MyExercisesInterface {
  sdk: IExerciserSDK | undefined;
  mappedWordList: Map<string, VocabWord[]>;
  mappedIdWordList: Map<string, VocabWord[]>;
}

type ActionMode = "list" | "create" | "edit";

function MyExercises({ sdk, mappedWordList, mappedIdWordList }: MyExercisesInterface) {
  const navigate = useNavigate();
  const query = useQuery();
  const { getFormattedString, APFormattedMessage } = useVocabIntl();

  const {
    profileData,
    setProfileOpen,
    setSettingModalOpen,
    skin,
    setSettingsTabIndex
  } = useSettingsContext();

  const [loading, setLoading] = useState(false);
  const [savingScenario, setSavingScenario] = useState<boolean>(false);
  const [senarios, setSenarios] = useState<any[]>([]);
  const [isLargeScreen] = useMediaQuery("(min-width: 764px)");
  const [mode, setMode] = useState<ActionMode>("list");
  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    isOpen: cancelConfirmOpen,
    onClose: closeCancelConfirm,
    onOpen: openCancelConfirm,
  } = useDisclosure();
  const [anonymousConfirmationOpen, setAnonymousConfirmationOpen] = useState<boolean>(false);
  const [scenarioName, setScenarioName] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [currentScenarioId, setCurrentScenarioId] = useState("");
  const [currentScenarioWords, setCurrentScenarioWords] = useState<VocabWord[]>(
    []
  );
  const [currentScenario, setCurrentScenario] = useState<VocabScenario | null>(null);
  const [sessionExerciseCount, setSessionExerciseCount] = useState<number>(0);
  const [sessionWordList, setSessionWordList] = useState<string[]>([]);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const currentUser = useRef<string>("");

  const handleOpenSettings = () => {
    setSettingModalOpen(true);
  };

  useEffect(() => { if (sdk) fetchSenarios() }, [sdk]);

  useEffect(() => {
    const handleSessionData = async () => {
      try {
        if (sdk) {
          const sessionData = await sdk.getSessionManager().getSessionData();
          if (sessionData.sessionExerciseCount === 0) {
            setSessionExerciseCount(0);
          } else {
            setSessionExerciseCount(sessionData.sessionExerciseCount);
          }
          if (sessionData.sessionWordList.length === 0) {
            setSessionWordList([]);
          } else {
            setSessionWordList(sessionData.sessionWordList);
          }
        }
      } catch (err) { }
    }
    if (sdk) {
      setIsLoggedIn(sdk._firebase.isLoggedIn());
      handleSessionData();
    }
  }, [sdk, sessionExerciseCount]);

  const fetchSenarios = async () => {
    if (!sdk) {
      return;
    }
    setLoading(true);
    const settings = await sdk.getDataManager().getSettings();
    currentUser.current = settings?.id;
    let mylist = settings && settings.mylist ? settings.mylist : [];
    mylist = mylist.filter((c: any, index: any) => {
      return mylist.indexOf(c) === index;
    });

    if (mylist) {
      try {
        //firebase has limit for where query as 10 count so should chunk into 10 items array
        let stagedList: string[][];
        if (mylist.length > 10) {
          stagedList = chunkArray(mylist, 10);
        } else {
          stagedList = [mylist];
        }

        const response: SquidexScenarioData[][] = await Promise.all(
          stagedList
            .filter(listChunk => listChunk.length > 0)
            .map(listChunk => sdk.getDataManager().getScenariosWithIDs(listChunk))
        );

        const scenarioList: SquidexScenarioData[] = response.reduce(
          (result, item) => result.concat(item),
          []
        );

        const guestScenarios: SquidexScenarioData[] = sdk.getFirebase().isLoggedIn() ?
          [] : JSON.parse(sessionStorage.getItem(guestScenariosStorageKey) || "[]");

        if (scenarioList && scenarioList.length > 0) {
          scenarioList.sort((scenario1, scenario2) => {
            return mylist.indexOf(scenario1.id!) - mylist.indexOf(scenario2.id!);
          });

          /**
           * filter deleted scenarios
           */
          const stagedScenarios: SquidexScenarioData[] = scenarioList
            .filter((scenario) => scenario && !scenario.deleted)
          const scenarios = [...guestScenarios.reverse(), ...stagedScenarios];
          setSenarios(scenarios);
        } else {
          setSenarios(guestScenarios);
        }
        setLoading(false);
      } catch (error) {
        console.log("error", error);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  const back = () => {
    const demo = query.get("demo");
    let url = "/game";

    if (demo && demo != "") {
      url += "?demo=" + demo;
    }

    navigate(url, { state: "back" });
  };

  const onStart = (scenario: string | VocabWord[], skipPreview?: boolean) => {
    if (typeof scenario === "string") {
      const demo = query.get("demo");
      let url = "/game?senarioId=" + scenario ?? currentScenario!.id;

      if (demo && demo != "") {
        url += "&demo=" + demo;
      }

      if (skipPreview) {
        url += "&skipPreview=true";
      }

      navigate(url, { state: "back" });
    }
  };

  const handleSetCreateMode = () => isLoggedIn ? onOpen() : setAnonymousConfirmationOpen(true);

  const handleCreate = async (name: string, tags: string[]) => {
    onClose();
    setMode("create");
    setCurrentScenario(new VocabScenario({ id: "", name, tags, wordsList: [] }))
    setScenarioName(name);
    setTags(tags);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    handleCancelCreate();
  };

  const handleSelectWords = async (words: VocabWord[]) => {
    if (!sdk) {
      return;
    }
    setSavingScenario(true);
    let scenarioId = currentScenarioId;
    const guestMode = !sdk.getFirebase().isLoggedIn();
    const data: SquidexScenarioData = {
      wordsList: words.map(wordItem => ({ id: wordItem.id })),
      version: 1,
      name: scenarioName,
      tags: tags,
      userType: profileData?.type
    };

    if (guestMode) {
      const uuid = new ShortUniqueId({ length: 10 });
      const inputId = scenarioId;
      scenarioId = inputId || 'guest_' + uuid();
      sdk.getAnalytics().push({
        action: inputId ? "update" : "add",
        event: "liste",
        id: scenarioId,
        total: words.length,
        type: "perso",
        platform: process.env.REACT_APP_PLATFORM
      });
      const guestScenarios = JSON.parse(sessionStorage.getItem(guestScenariosStorageKey) || "[]");
      const scenarioIndex = guestScenarios.findIndex((scenario: any) => scenario.id === scenarioId);
      if (scenarioIndex >= 0) guestScenarios[scenarioIndex] = { ...data, id: scenarioId };
      sessionStorage.setItem(
        guestScenariosStorageKey,
        JSON.stringify(scenarioIndex >= 0 ? guestScenarios : [...guestScenarios, { ...data, id: scenarioId }])
      );
      setCurrentScenarioId(scenarioId);
      setCurrentScenarioWords(words);
      setSavingScenario(false);
      setPreviewOpen(true);
    } else {
      const response = await sdk
        .getDataManager()
        .saveScenario({ name: scenarioId, data, newScenario: !scenarioId });

      if (response.data) {

        sdk.getAnalytics().push({
          action: scenarioId ? "update" : "add",
          event: "liste",
          id: response.data.scenarioId,
          total: words.length,
          type: "perso",
          platform: process.env.REACT_APP_PLATFORM
        });
        if (response.data.scenarioId) {
          scenarioId = response.data.scenarioId;
          setCurrentScenarioId(response.data.scenarioId);
          await saveToMyList(sdk, response.data.scenarioId);
        }

        setCurrentScenarioWords(words);
        setPreviewOpen(true);
        // onStart(response.data.scenarioId, true);
      }
      setSavingScenario(false);
    }
    setCurrentScenario(new VocabScenario({ id: scenarioId, name: data.name, tags: data.tags, wordsList: words }))
  };

  const resetCreateData = () => {
    setMode("list");
    setCurrentScenarioId("");
    setCurrentScenarioWords([]);
    setScenarioName("");
    setTags([]);
    setCurrentScenario(null)
  }

  const gotoScenarioList = () => {
    setPreviewOpen(false);
    resetCreateData();
    fetchSenarios();
  };

  const handleEditPrimary = () => {
    setMode("edit");
    onOpen();
  };

  const renderTitle = useMemo(() => {
    if (mode === "list") {
      return (
        <Text
          sx={{
            fontSize: ["1rem", "1.5rem"],
            fontWeight: 900,
            color: "black",
            textAlign: "center",
            whiteSpace: "nowrap"
          }}
        >
          <APFormattedMessage id={"my-exercises-title"} />
        </Text>
      );
    } else {
      return (
        <VStack>
          <HStack justifyContent={"center"} gap={4}>
            <Text fontSize={["1rem", "1.75rem"]} fontWeight={"bold"} textAlign={"center"}>
              {scenarioName}
            </Text>
            <APIconButton
              sx={{
                color: "black",
                background: "transparent",
                paddingInlineStart: 0,
                paddingInlineEnd: 0,
              }}
              onClick={handleEditPrimary}
              aria-label={getFormattedString('aria-label-edit-title')}
            ><FiEdit size={24} /></APIconButton>
          </HStack>

          <Text fontSize={["0.5rem", "0.875rem"]} textAlign={"center"}>
            {getTagLabels(tags).join(", ")}
          </Text>
        </VStack>
      );
    }
  }, [mode, scenarioName, tags]);

  const handleStartScenario = async (scenId: string) => {
    // const response = await sdk.getDataManager().getScenario(scenId);
    // if (response) {
    //   setCurrentScenarioId(response.id);
    //   setCurrentScenarioWords(response.wordsList);
    //   setPreviewOpen(true);
    //   setScenarioName(response.name);
    //   setTags(response.tags);
    // }
    onStart(scenId);
  };

  const handleEditScenario = async (scenarioId: string) => {
    if (sdk) {
      const scenario = scenarioId?.includes("guest") ?
        JSON.parse(sessionStorage.getItem(guestScenariosStorageKey) || "[]").find((scenario: any) => scenario.id === scenarioId) :
        await sdk.getDataManager().getScenario(scenarioId)
      if (scenario) {
        setCurrentScenarioId(scenario.id);
        setCurrentScenarioWords(resolveWordList(mappedIdWordList, scenario.wordsList));
        setScenarioName(scenario.name);
        setTags(scenario.tags);
        setMode("create");
      }
    }
  };

  const handleDeleteScenario = async (scenarioId: string): Promise<string> => {
    return new Promise(async (resolve, reject) => {
      if (!sdk) {
        reject("SDK not initialized")
        return;
      }
      if (scenarioId?.includes("guest")) {
        const guestScenarios = JSON.parse(sessionStorage.getItem(guestScenariosStorageKey) || "[]");
        const updatedScenarios = [...guestScenarios].filter((senario: any) => senario?.id !== scenarioId);
        sessionStorage.setItem(guestScenariosStorageKey, JSON.stringify(updatedScenarios));
        fetchSenarios();
        sdk.getAnalytics().push({
          action: "delete",
          event: "liste",
          id: scenarioId,
          type: "perso",
          platform: process.env.REACT_APP_PLATFORM
        });
        resolve('success');
      }
      try {
        const response = await sdk
          .getDataManager()
          .deleteScenario({ scenarioId: scenarioId });

        sdk.getAnalytics().push({
          action: "delete",
          event: "liste",
          id: scenarioId,
          type: "perso",
          platform: process.env.REACT_APP_PLATFORM
        })

        if (response && response.data && response.data.success === true) {
          fetchSenarios();
          resolve('success');
        } else {
          reject('error');
        }
      } catch (error) {
        reject('error')
      }
    });
  };

  const handleClickModify = () => {
    setMode("create");
    setPreviewOpen(false);
  };

  const handleBack = () => {
    if (mode === "list") {
      back();
    } else {
      openCancelConfirm();
    }
  };

  const handleConfirmCancel = () => {
    closeCancelConfirm();
    handleCancelCreate();
  };

  const handleCancelCreate = () => {
    onClose();
    resetCreateData();
  };

  return (
    <>
      <Profile
        sdk={sdk}
        sessionData={{
          sessionWordList: sessionWordList,
          sessionExerciseCount: sessionExerciseCount
        }}
      />
      <Flex
        className={"main-content"}
        direction={"column"}
        bgColor={skin?.data?.color?.iv}
        bgImage={skin?.data?.background ? `url(${skin.data.background})` : `var(--ap-theme-default)`}
        bgPosition={"center"}
        bgSize={"cover"}
        bgRepeat={"no-repeat"}
      >
        <Flex layerStyle={"pageContainer"}>
          <NavTopBar
            backIcon={mode === "list" ?
              <Back style={{ width: "1em", height: "1em" }} /> :
              <Close style={{ width: "1em", height: "1em" }} />}
            handleBack={handleBack}
            backAriaLabel={getFormattedString('aria-label-back-to-main-menu')}
            title={renderTitle}
            openProfile={() => setProfileOpen(true)}
            openSettings={() => handleOpenSettings()}
          />
          <Box bg={"white"} borderRadius={"2xl"} maxWidth={"container.sm"} flexGrow={1}
               width={"100%"} alignSelf={"center"} overflow={"hidden"}>
            {mode === "list" && (
              <Box display={"flex"} borderRadius={"2xl"} gap={1} flexDirection={"column"} padding={4} width={"100%"}
                   height={"100%"} maxHeight={"100%"}>
                <VStack alignItems={"stretch"} overflowY={"auto"} paddingX={"0.5rem"} marginX={"-0.5rem"}>
                  <Button
                    border={"dashed 2px"}
                    borderColor={"#51b6c2"}
                    height={"112px"}
                    width={"100%"}
                    flexDirection={"column"}
                    sx={{
                      bg: "transparent",
                      cursor: "pointer",
                      _hover: {
                        bg: "blackAlpha.200",
                      },
                      _focus: {
                        bg: "blackAlpha.200",
                      }
                    }}
                    style={{ borderRadius: 16 }}
                    onClick={handleSetCreateMode}
                    tabIndex={1}
                    autoFocus
                    aria-label={getFormattedString('aria-label-create-exercice')}
                  >
                    <Center
                      color={"white"}
                      background={"cyan.550"}
                      height={"3rem"}
                      width={"3rem"} fontSize={"1rem"}
                      borderRadius="1.5rem"
                    >
                      <Plus />
                    </Center>
                    <Text
                      fontSize={16}
                      lineHeight={1.5}
                      fontWeight={"bold"}
                      color={"cyan.550"}
                    >
                      <APFormattedMessage id={"create-exercise-button"} />
                    </Text>
                  </Button>

                  <Grid flexGrow={1}
                        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }}
                        sx={{ mt: "2rem", mb: senarios.length && !isLoggedIn ? "2rem" : 0 }}
                        columnGap={2}
                        rowGap={2}
                  >
                    {!loading &&
                      senarios.map((senario: any, index) => (
                        <GridItem key={senario.id} rowSpan={currentUser.current === senario.AUTHOR_ID ? 2 : 1}>
                          <ScenarioCard
                            name={senario.name}
                            tags={getTagLabels(senario.tags)}
                            id={senario.id}
                            onStart={handleStartScenario}
                            onEdit={currentUser.current === senario.AUTHOR_ID ? handleEditScenario : undefined}
                            onDelete={currentUser.current === senario.AUTHOR_ID ? handleDeleteScenario : undefined}
                            tabIndex={index + 2}
                            anonymous={!isLoggedIn && currentUser.current === senario.AUTHOR_ID}
                          />
                        </GridItem>
                      ))}
                  </Grid>
                </VStack>
                <Spacer flex={"1 1 auto"} />
                {!isLoggedIn && (
                  <Box sx={{
                    w: "100%",
                    bg: "#FCFCFA",
                    border: "1px solid #E7E1D8",
                    borderRadius: "16px",
                  }}>
                    <CallToLoginView heading={"lists-wont-save-heading"} />
                  </Box>
                )}
              </Box>
            )}
            {(mode === "create" || mode === "edit") && (
              <WordsSelection
                key={"words-selection"}
                sdk={sdk}
                saving={savingScenario}
                onSelectWords={handleSelectWords}
                scenarioWords={currentScenarioWords}
                mappedWordList={mappedWordList}
              />
            )}
          </Box>
          <APFooter />
          <APCommonDialog
            open={anonymousConfirmationOpen}
            title={getFormattedString("attention")}
            onNext={() => {
              setAnonymousConfirmationOpen(false);
              onOpen();
            }}
            onClose={() => setAnonymousConfirmationOpen(false)}
            error={false}
            btnTitle={getFormattedString("continue-button")}
            footer={
              <Box
                sx={{
                  display: "flex",
                  columnGap: 2,
                  width: "100%",
                  justifyContent: "space-between",
                }}>
                <APButton onClick={() => navigate("/", { replace: true, state: { instantLogin: true } })}>
                  <APFormattedMessage id={"login"} />
                </APButton>
                <APButton variant={"raisedOutline"} colorScheme={"tertiary"}
                          onClick={() => {
                            onOpen();
                            setAnonymousConfirmationOpen(false);
                          }}>
                  <APFormattedMessage id={"continue-button"} />
                </APButton>
              </Box>
            }>
            <Text textAlign={"center"}>
              <APFormattedMessage id={"anonymous-warning"} />
            </Text>
          </APCommonDialog>
          <ScenarioAddModal
            open={isOpen}
            onClose={mode === "edit" ? onClose : handleCancelCreate}
            onCreate={handleCreate}
            scenarioName={scenarioName}
            tags={tags}
            editMode={mode === "edit"}
          />
          <CancelConfirmModal
            open={cancelConfirmOpen}
            onClose={closeCancelConfirm}
            onConfirm={handleConfirmCancel}
            title={'close-create-exercise-modal-title'}
            description={'close-create-exercise-modal-description'}
            btnTitle={'close-create-exercise-modal-quit-button'}
          />
          <ScenarioPreviewModal
            open={previewOpen}
            scenario={currentScenario}
            listDescription={<APFormattedMessage id={"content-of-exercise"} />}
            onStart={onStart}
            onReturn={gotoScenarioList}
            returnTitle={getFormattedString("my-exercises-button")}
            onModify={handleClickModify}
          />
        </Flex>
      </Flex>
    </>
  );
}

export default MyExercises;
